import type {
    TableCreationResult,
    TableCreationResultWithColumns,
} from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { ColumnTemplate, MaybeConcreteColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import type { FormattableColumn, FormattableGridSettings } from '@thinkalpha/ui-types/contracts/display/settings';
import type { PredefinedColumnDefinition } from 'src/features/table/next/predefinedGridAdapter';

export type StaticGridColumn = FormattableColumn & PredefinedColumnDefinition;

export interface StaticGridSettings extends FormattableGridSettings {
    columns: StaticGridColumn[];
    type: 'SimpleGrid';
}

// So that we can detect on type
export type FormattableGrid = StaticGridSettings | ColumnTemplate | FormattableGridSettings;

export type FetchedEntityType = 'default' | 'referenced' | 'concrete';

export interface FetchableTableCreationResult extends TableCreationResult {
    /** Client request mode, preserved even when the server returns an index over a log table. */
    isScrolling?: boolean;
    /**
     * How the table client should fetch data for this table
     */
    fetchMode: 'snapshot' | 'diff' | undefined;
}

export interface FetchableTableCreationResultWithColumns extends Omit<TableCreationResultWithColumns, 'tableCookie'> {
    /** Client request mode, preserved even when the server returns an index over a log table. */
    isScrolling?: boolean;
    /**
     * Deliberately made optional
     */
    tableCookie?: string;
    /**
     * How the table client should fetch data for this table
     */
    fetchMode: 'snapshot' | 'diff' | undefined;
}

export type FullKeyType = FetchableTableCreationResultWithColumns | FetchableTableCreationResult | null;

export const isStaticGridSettings = (settings: FormattableGrid): settings is StaticGridSettings =>
    'type' in settings && settings.type === 'SimpleGrid';

export function makeColumnTemplateEqualityKey(
    columnTemplate: Pick<ColumnTemplate, 'columns'> | undefined,
): string | undefined {
    if (!columnTemplate) return columnTemplate;

    const relevantKeys = columnTemplate.columns
        .map((x) => {
            const dataKey =
                x.type === 'formula' ? x.formula : x.type === 'indicator' ? x.indicator.id : `client:${x.clientKey}`;
            return [x.id, dataKey].join('::');
        })
        .sort();

    return relevantKeys.join('||');
}

/**
 * @deprecated
 */
export function makeColumnTemplateEqualityKeyWithSort(columnTemplate: ColumnTemplate | undefined): string | undefined {
    if (!columnTemplate) return columnTemplate;

    const relevantKeys = columnTemplate.columns
        .map((x) => {
            const dataKey =
                x.type === 'formula' ? x.formula : x.type === 'indicator' ? x.indicator.id : `client:${x.clientKey}`;
            return [x.id, x.sort, x.sortIndex, dataKey].join('::');
        })
        .sort();

    return relevantKeys.join('||');
}

export function extractColumnTemplateForPayload(
    currentColumnTemplate: MaybeConcreteColumnTemplate,
    columnTemplateType: FetchedEntityType,
): string | MaybeConcreteColumnTemplate {
    function mapColumnTemplateToPayload(ct: MaybeConcreteColumnTemplate): MaybeConcreteColumnTemplate {
        return {
            columns: ct.columns,
            filter: ct.filter,
            key: ct.key,
            name: ct.name,
            gridDisplaySettings: ct.gridDisplaySettings,
            isTemplate: ct.isTemplate,
            rowBasedDisplaySettings: ct.rowBasedDisplaySettings,
            id: ct.id,
        };
    }

    if (columnTemplateType === 'default') return mapColumnTemplateToPayload(currentColumnTemplate);
    if (columnTemplateType === 'referenced' && currentColumnTemplate.id) return currentColumnTemplate.id;

    return mapColumnTemplateToPayload(currentColumnTemplate);
}

export function makePlatformsEqualityKey(platforms?: string[] | null) {
    return platforms?.join?.('__') ?? 'null_platforms';
}

export function makeAccountsEqualityKey(accounts?: string[] | null) {
    return accounts?.join?.('__') ?? 'null_accounts';
}

export function makeUsersEqualityKey(userIds?: string[] | null) {
    return userIds?.join('__') ?? 'null_users';
}

export type ToolbarClockTimeFormat = 'H:mm:ss' | 'h:mm:ss a';
